import { useDispatch } from "react-redux";
import analytics from "web/script/analytics/analytics";
import browser from "web/script/utils/browser";
import classnames from "classnames";
import environment from "web/script/modules/environment";
import PropTypes from "prop-types";
import { gettext } from "web/script/modules/django-i18n";
import React, { useCallback, useEffect, useRef, useState } from "react";

import storage from "web/script/utils/storage";
import { useSmallBreakpoint } from "web/react/hooks/use-breakpoint/use-breakpoint";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import FollowDesignerButton from "web/react/components/follow-designer-button/follow-designer-button";
import { bannerDisabledByUser } from "web/redux/ducks/follow-designer-banner";
import styles from "./follow-sticky-banner.css";

const ONE_HOUR = 3600000;

function FollowStickyBanner({
    lystId,
    followedText,
    notFollowedText,
    isAuthenticated,
    isFollowing,
    description,
    profileImage,
    forceDisplay,
}) {
    const [show, setShow] = useState(false);
    let [closed, setClosed] = useState(false);
    const wrapperRef = useRef(null);
    const [isIos, setIsIos] = useState(false);
    const pageSubType = environment.get("pageSubType");
    const dispatch = useDispatch();

    // This is to check if to hide the banner when Follow button
    // in the top is visible
    const hideDUBanner = !useSmallBreakpoint();
    const useIosDUText =
        isIos &&
        pageSubType.startsWith("designer") &&
        environment.getFeature("mg_du_banner_ios_to_app");

    function displayBanner() {
        setShow(true);
        analytics.event("du_feed_banner", "banner_shown", "du_banner", true); // non interactive
        storage.remove("hideStickyBannerTimestamp");
    }

    const triggerBanner = useCallback(
        (delay) => {
            let hideStickyBannerTimestamp = storage.get("hideStickyBannerTimestamp");
            let timer;
            if (
                !hideStickyBannerTimestamp ||
                hideStickyBannerTimestamp < new Date().getTime() - ONE_HOUR
            ) {
                timer = setTimeout(displayBanner, delay);
            } else {
                dispatch(bannerDisabledByUser(true));
            }

            return () => {
                clearTimeout(timer);
            };
        },
        [dispatch]
    );

    useEffect(() => {
        setIsIos(browser.ios);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [browser.ios]);

    useEffect(() => {
        if (closed || hideDUBanner) {
            return;
        }
        triggerBanner(5000);
    });

    useEffect(() => {
        if (closed || !hideDUBanner) {
            return;
        }

        if (forceDisplay) {
            triggerBanner(500);
        } else {
            setShow(false);
        }
    }, [forceDisplay, closed, triggerBanner, hideDUBanner]);

    function triggerClose() {
        setShow(false);
        setClosed(true);
        // update reducer state banner is disabled
        dispatch(bannerDisabledByUser(true));
        storage.set("hideStickyBannerTimestamp", new Date().getTime());
        analytics.event("du_feed_banner", "banner_hidden", "du_banner", true); // non interactive
    }
    const bannerClassNames = classnames(styles.stickyBanner, {
        [styles.stickyBannerShow]: show && !closed,
    });

    let renderedProfileImage = "";
    if (profileImage) {
        renderedProfileImage = <img src={profileImage} className={styles.imageProfile} />;
    }

    return (
        <div ref={wrapperRef}>
            <div className={bannerClassNames}>
                <div className={styles.wrapper}>
                    <div>
                        {renderedProfileImage}
                        <div
                            className={styles.content}
                            dangerouslySetInnerHTML={{ __html: description }}
                        />
                    </div>
                    <div>
                        <FollowDesignerButton
                            className={styles.button}
                            lystId={lystId}
                            followedText={followedText}
                            notFollowedText={
                                useIosDUText
                                    ? // "Download the Lyst App"
                                      gettext("app_download_large_banner.general.cta")
                                    : notFollowedText
                            }
                            isAuthenticated={isAuthenticated}
                            isFollowing={isFollowing}
                            description={description}
                            analyticsParameters={[
                                "du_feed_banner",
                                "click_banner_cta",
                                "du_banner",
                            ]}
                        />
                        <button className={styles.closeButton} onClick={triggerClose}>
                            <SVGIcon name="cross" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default FollowStickyBanner;

FollowStickyBanner.propTypes = {
    lystId: PropTypes.number,
    followedText: PropTypes.string,
    notFollowedText: PropTypes.string,
    isAuthenticated: PropTypes.string,
    isFollowing: PropTypes.string,
    description: PropTypes.string,
    profileImage: PropTypes.string,
    forceDisplay: PropTypes.bool,
};
